import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
  TableContainer,
  Paper,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from "@mui/material";
import { NeoButton } from "../shared/components";
import { useGuides } from "../hooks/useGuide";
import { useIntegration } from "../hooks/useIntegration";
import { Customer } from "types/integration";
import { isMspOrgCustomer } from "../utils/mspOrgCustomer";
import GlobalLoadingOverlay from "../components/GlobalLoadingOverlay";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import { format } from "date-fns";
import { useUserMsp } from "../hooks/useUserMsp";
import GuideApi from "../apis/guideApi";
import LayeredAutocompleteSelect from "../components/LayeredAutocompleteSelect";

const Guides: React.FC = () => {
  const { guides, fetchGuides, isLoading, selectedCustomerId, setCustomerSelection } = useGuides();
  const {
    customers,
    isLoading: customersLoading,
    getCustomers,
  } = useIntegration();
  const { userMspInfo } = useUserMsp();
  const navigate = useNavigate();
  const hasInitialized = useRef(false);
  
  // Reindex state
  const [isReindexing, setIsReindexing] = useState(false);
  const [reindexError, setReindexError] = useState<string | null>(null);
  const [reindexStatusText, setReindexStatusText] = useState<string>("Idle");
  const [reindexCompletedAt, setReindexCompletedAt] = useState<string | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const guideApi = useMemo(() => new GuideApi(), []);

  // Filter customers by MSP ID and include 'All' option
  const filteredCustomers = useMemo(() => {
    const allCustomers = (customers?.rows || customers || []) as Customer[];
    const mspCustomers = allCustomers.filter(
      (c: Customer) =>
        c.name?.toLowerCase() === "root" ||
        String((c as any).msp_id) === String(userMspInfo?.msp_id)
    );
    // Ensure 'All' (root) is first
    const rootCustomers = mspCustomers.filter((c: Customer) => c.name?.toLowerCase() === "root");
    const otherCustomers = mspCustomers.filter((c: Customer) => c.name?.toLowerCase() !== "root" && !isMspOrgCustomer(c));
    return [...rootCustomers, ...otherCustomers];
  }, [customers, userMspInfo?.msp_id]);

  // Create a map from customer id to name for fast lookup
  const customerMap = useMemo(() => {
    const map: Record<string, string> = {};
    filteredCustomers.forEach((c: Customer) => {
      if (c && c.id) {
        map[c.id] = c.name?.toLowerCase() === "root" ? "All Customer" : c.name;
      }
    });
    return map;
  }, [filteredCustomers]);

  // Get root customer ID (for "All" option)
  const rootCustomerId = useMemo(() => {
    const root = filteredCustomers.find((c: Customer) => c.name?.toLowerCase() === "root");
    return root?.id?.toString() || null;
  }, [filteredCustomers]);

  // Initialize: Load customers and set default selection
  useEffect(() => {
    getCustomers();
  }, [getCustomers]);

  // Set initial customer selection (only once when customers are loaded)
  useEffect(() => {
    if (!hasInitialized.current && filteredCustomers.length > 0 && rootCustomerId) {
      hasInitialized.current = true;
      if (!selectedCustomerId) {
        setCustomerSelection(rootCustomerId);
      }
    }
  }, [filteredCustomers.length, rootCustomerId, selectedCustomerId, setCustomerSelection]);

  // Fetch guides when customer selection changes
  useEffect(() => {
    if (!selectedCustomerId) {
      return;
    }

    // If "All" (root) is selected, pass undefined to show all guides
    const isAllSelected = selectedCustomerId === rootCustomerId || selectedCustomerId === "all";
    const customerIdToFetch = isAllSelected ? undefined : selectedCustomerId;
    
    fetchGuides(customerIdToFetch);
  }, [selectedCustomerId, rootCustomerId, fetchGuides]);

  // Start polling for job status
  const startPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      return; // Already polling
    }
    
    pollingIntervalRef.current = setInterval(async () => {
      try {
        const status = await guideApi.getReindexStatus("guide");
        setReindexStatusText(status?.ingestion?.status || "Idle");
        setReindexCompletedAt(status?.ingestion?.completed_at || null);
        
        if (!status.isRunning) {
          setIsReindexing(false);
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
          // Refresh guides after reindex completes
          if (selectedCustomerId) {
            const isAllSelected = selectedCustomerId === rootCustomerId || selectedCustomerId === "all";
            const customerIdToFetch = isAllSelected ? undefined : selectedCustomerId;
            fetchGuides(customerIdToFetch);
          }
        }
      } catch (error) {
        console.error("Error polling reindex status:", error);
      }
    }, 5000); // Poll every 5 seconds
  }, [guideApi, selectedCustomerId, rootCustomerId, fetchGuides]);

  // Stop polling
  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  }, []);

  // Check reindex job status
  const checkReindexStatus = useCallback(async () => {
    try {
      const status = await guideApi.getReindexStatus("guide");
      setReindexStatusText(status?.ingestion?.status || "Idle");
      setReindexCompletedAt(status?.ingestion?.completed_at || null);
      
      if (status.isRunning) {
        setIsReindexing(true);
        // Start polling if not already polling
        if (!pollingIntervalRef.current) {
          startPolling();
        }
      } else {
        setIsReindexing(false);
        stopPolling();
      }
    } catch (error: any) {
      console.error("Error checking reindex status:", error);
      // Don't show error on initial check, just log it
    }
  }, [guideApi, startPolling, stopPolling]);

  // Check reindex status on mount and when page becomes visible
  useEffect(() => {
    checkReindexStatus();
    
    // Also check when page becomes visible again (user switches tabs or returns to page)
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        checkReindexStatus();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Cleanup polling on unmount
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [checkReindexStatus]);

  // Show confirmation dialog
  const handleReindexClick = () => {
    setShowConfirmDialog(true);
  };

  // Trigger reindex after confirmation
  const handleReindexConfirm = async () => {
    setShowConfirmDialog(false);
    try {
      setReindexError(null);
      setIsReindexing(true);
      
      await guideApi.triggerReindex("guide");
      
      // Start polling for status
      startPolling();
    } catch (error: any) {
      console.error("Error triggering reindex:", error);
      setReindexError(error.response?.data?.error || "Failed to trigger reindex");
      setIsReindexing(false);
    }
  };

  // Cancel reindex
  const handleReindexCancel = () => {
    setShowConfirmDialog(false);
  };

  // Prepare customer options for dropdown
  const customerOptions = useMemo(() => {
    return filteredCustomers.map((customer: Customer) => ({
      label: customer.name?.toLowerCase() === "root" ? "All Customer" : customer.name,
      value: customer.id.toString(),
    }));
  }, [filteredCustomers]);

  // Ensure guides is always an array
  const guidesArray = Array.isArray(guides) ? guides : [];

  return (
    <>
      {(isLoading || customersLoading) && <GlobalLoadingOverlay />}
      <Box sx={{ px: 1, pt: 2, width: "100%", fontFamily: "var(--theo-font-sans)" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            mb: 2,
            gap: 2,
            justifyContent: "space-between",
          }}
        >
          {/* Left side — Refresh (KB ingestion) button */}
          <NeoButton
            variant="neo"
            onClick={handleReindexClick}
            disabled={isReindexing}
            sx={{
              minWidth: 120,
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            {isReindexing ? (
              <>
                <CircularProgress size={16} sx={{ color: "white" }} />
                Refreshing...
              </>
            ) : (
              <>
                <RefreshIcon fontSize="small" />
                Refresh
              </>
            )}
          </NeoButton>

          {/* Right side - Customer dropdown and Add Guide button */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <LayeredAutocompleteSelect
              label="Customer"
              value={selectedCustomerId || ""}
              onChange={setCustomerSelection}
              options={customerOptions}
              disableClearable
              sx={{ minWidth: 180 }}
              textFieldSx={{
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#E0E0E0",
                },
                "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#705D4A",
                },
                "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#705D4A",
                },
              }}
            />
            <NeoButton
              variant="neo"
              onClick={() => {
                // If "All Customer" (root) is selected, pass "all" to indicate no customer should be preselected
                const isAllSelected = selectedCustomerId === rootCustomerId || selectedCustomerId === "all";
                navigate("/guides/new", { 
                  state: { selectedCustomer: isAllSelected ? "all" : selectedCustomerId } 
                });
              }}
            >
              Add Guide
            </NeoButton>
          </Box>
        </Box>
        {reindexError && (
          <Box
            sx={{
              mb: 2,
              p: 1.5,
              backgroundColor: "#FEE",
              borderRadius: 1,
              border: "1px solid #F88",
            }}
          >
            <Typography sx={{ color: "#C33", fontSize: 14, fontFamily: "var(--theo-font-sans)" }}>
              {reindexError}
            </Typography>
          </Box>
        )}
        {!reindexError && (
          <Box sx={{ mb: 2 }}>
            <Typography sx={{ color: "#666", fontSize: 13, fontFamily: "var(--theo-font-sans)" }}>
              Status: {reindexStatusText}
              {reindexCompletedAt ? ` | Last completed: ${new Date(reindexCompletedAt).toLocaleString()}` : ""}
            </Typography>
          </Box>
        )}

        {/* Confirmation Dialog */}
        <Dialog
          open={showConfirmDialog}
          onClose={handleReindexCancel}
          aria-labelledby="reindex-dialog-title"
          aria-describedby="reindex-dialog-description"
        >
          <DialogTitle id="reindex-dialog-title" sx={{ fontFamily: "var(--theo-font-sans)" }}>
            Confirm refresh
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="reindex-dialog-description" sx={{ fontFamily: "var(--theo-font-sans)" }}>
              Do you want to continue?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleReindexCancel} sx={{ fontFamily: "var(--theo-font-sans)", color: "#666" }}>
              Cancel
            </Button>
            <Button 
              onClick={handleReindexConfirm} 
              variant="contained"
              sx={{ 
                fontFamily: "var(--theo-font-sans)",
                backgroundColor: "#705D4A",
                "&:hover": {
                  backgroundColor: "#5A4A3A",
                }
              }}
              autoFocus
            >
              Confirm
            </Button>
          </DialogActions>
        </Dialog>
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            borderRadius: 2,
            boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
            border: "1px solid #E0E0E0",
            maxHeight: "calc(100vh - 180px)",
            overflowY: "auto",
          }}
        >
          <Table stickyHeader sx={{ minWidth: 700, fontFamily: "var(--theo-font-sans)" }}>
            <TableHead>
              <TableRow sx={{ background: "#F7F7F7" }}>
                <TableCell
                  sx={{
                    textAlign: "center",
                    fontFamily: "var(--theo-font-sans)",
                    fontSize: "12px",
                    fontWeight: 500,
                    color: "#838383",
                    cursor: "pointer",
                  }}
                >
                  Description
                </TableCell>
                <TableCell
                  sx={{
                    textAlign: "center",
                    fontFamily: "var(--theo-font-sans)",
                    fontSize: "12px",
                    fontWeight: 500,
                    color: "#838383",
                    cursor: "pointer",
                  }}
                >
                  Platform
                </TableCell>
                <TableCell
                  sx={{
                    textAlign: "center",
                    fontFamily: "var(--theo-font-sans)",
                    fontSize: "12px",
                    fontWeight: 500,
                    color: "#838383",
                    cursor: "pointer",
                  }}
                >
                  Type
                </TableCell>
                <TableCell
                  sx={{
                    textAlign: "center",
                    fontFamily: "var(--theo-font-sans)",
                    fontSize: "12px",
                    fontWeight: 500,
                    color: "#838383",
                    cursor: "pointer",
                  }}
                >
                  Created At
                </TableCell>
                <TableCell
                  sx={{
                    textAlign: "center",
                    fontFamily: "var(--theo-font-sans)",
                    fontSize: "12px",
                    fontWeight: 500,
                    color: "#838383",
                    cursor: "pointer",
                  }}
                >
                  Created By
                </TableCell>
                <TableCell
                  sx={{
                    textAlign: "center",
                    fontFamily: "var(--theo-font-sans)",
                    fontSize: "12px",
                    fontWeight: 500,
                    color: "#838383",
                    cursor: "pointer",
                  }}
                >
                  Customer
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {guidesArray.map((guide: any, idx: number) => (
                <TableRow
                  key={guide.id}
                  onClick={() => navigate(`/guides/${guide.id}`)}
                  sx={{
                    background: idx % 2 === 0 ? "#FAFAFA" : "#FFF",
                    "&:hover": { background: "#F0F0F0" },
                    transition: "background 0.2s",
                    cursor: "pointer",
                  }}
                >
                  <TableCell
                    sx={{
                      py: 1.5,
                      fontSize: 14,
                      fontFamily: "var(--theo-font-sans)",
                      textAlign: "center",
                      position: "relative",
                    }}
                  >
                    {guide.description}
                  </TableCell>
                  <TableCell
                    sx={{
                      py: 1.5,
                      fontSize: 14,
                      fontFamily: "var(--theo-font-sans)",
                      textAlign: "center",
                    }}
                  >
                    {guide.platform}
                  </TableCell>
                  <TableCell
                    sx={{
                      py: 1.5,
                      fontSize: 14,
                      fontFamily: "var(--theo-font-sans)",
                      textAlign: "center",
                    }}
                  >
                    {guide.type}
                  </TableCell>
                  <TableCell
                    sx={{
                      py: 1.5,
                      fontSize: 14,
                      fontFamily: "var(--theo-font-sans)",
                      textAlign: "center",
                    }}
                  >
                    {guide.date_added
                      ? format(new Date(guide.date_added), "PPP")
                      : ""}
                  </TableCell>
                  <TableCell
                    sx={{
                      py: 1.5,
                      fontSize: 14,
                      fontFamily: "var(--theo-font-sans)",
                      textAlign: "center",
                    }}
                  >
                    {guide.created_by}
                  </TableCell>
                  <TableCell
                    sx={{
                      py: 1.5,
                      fontSize: 14,
                      fontFamily: "var(--theo-font-sans)",
                      textAlign: "center",
                    }}
                  >
                    {guide.customer_id
                      ? customerMap[guide.customer_id.toString()] || ""
                      : ""}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {guidesArray.length === 0 && !isLoading && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minHeight: 220,
              color: "#9B7AEF",
              opacity: 0.85,
              py: 4,
              fontFamily: "var(--theo-font-sans)",
            }}
          >
            <MenuBookOutlinedIcon sx={{ fontSize: 48, mb: 1 }} />
            <Typography
              variant="h6"
              sx={{ fontWeight: 500, color: "#705D4A", fontFamily: "var(--theo-font-sans)" }}
            >
              No guides found
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "#6C6C6C", mt: 1, fontFamily: "var(--theo-font-sans)" }}
            >
              Click <b>Add Guide</b> to create your first guide.
            </Typography>
          </Box>
        )}
      </Box>
    </>
  );
};

// Add this style globally for the hover effect
const style = document.createElement("style");
style.innerHTML = `
  .MuiTableRow-root:hover .guide-link-icon {
    opacity: 1 !important;
    pointer-events: auto;
  }
`;
document.head.appendChild(style);

export default Guides;
